import { useQuery } from "@apollo/client";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select, { MultiValue, SingleValue } from "react-select";

import {
  CustomerBasic,
  GET_CUSTOMERS_BASIC,
  renderCustomerType,
} from "../../graphql/sales/customers";
import { Avatar } from "../appearance";
import { ButtonPopover, PopoverPosition } from "../appearance";
import { selectStyles, SelectWrapper } from ".";

type FieldCustomerProps = {
  title: string;
  value: number | null;
  onChange?: (newValue: number | null) => void;
  className?: string;
  isPreview?: boolean;
  position?: PopoverPosition;
};
export function FieldCustomer({
  title,
  value: initialValue,
  onChange,
  className = "",
  isPreview = false,
  position = PopoverPosition.RIGHT,
}: FieldCustomerProps) {
  const [value, setValue] = useState<SingleValue<OptionProps>>(null);

  const { data, loading, refetch } = useQuery<{
    fetchCustomers: CustomerBasic[];
  }>(GET_CUSTOMERS_BASIC);

  const options: MultiValue<OptionProps> = useMemo(() => {
    if (data?.fetchCustomers) {
      return data.fetchCustomers?.map((p: CustomerBasic) => {
        return {
          label: p.customerName,
          value: p.id,
        };
      });
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (!initialValue) return setValue(null);
    const isInteger = Number.isInteger(initialValue);
    if (isInteger) {
      const option =
        options.find((option) => option.value === initialValue.toString()) ??
        null;
      setValue(option);
      return;
    }
    setValue(null);
  }, [options, initialValue]);

  const onChangeHandler = (newValue: SingleValue<OptionProps>) => {
    typeof onChange === "function" &&
      onChange(newValue ? Number(newValue.value) : null);
  };

  if (isPreview) {
    if (value) {
      const user = data?.fetchCustomers?.find(
        (p: CustomerBasic) => p.id === value?.value
      );
      if (user) {
        const fullName = user.customerName;
        let firstName,
          lastName = "";
        if (fullName) {
          const names = fullName.split(" ");
          firstName = names[0];
          lastName = names[names.length - 1];
        }
        return (
          <ButtonPopover
            title={fullName}
            child={
              <div className="flex items-center">
                <Avatar firstName={firstName} lastName={lastName} />
                <span className="ml-2">{fullName}</span>
              </div>
            }
            position={position}
          >
            <div className="px-2.5 py-2 md:px-4 md:py-3 xl:px-5 xl:pb-3 xl:pt-5">
              <h3 className="mb-1 font-medium">{fullName}</h3>
              <p>{user.customerCode}</p>
              <p>{renderCustomerType(user.customerType)}</p>
              <p>{user.email}</p>
            </div>
            <div className="rounded-bl-2xl rounded-br-2xl border-t border-gray-300 bg-greyish px-1.5 py-2 md:px-2.5 md:py-3">
              <Link
                to={`/sales/customers/${user.id}`}
                target="_blank"
                className="block text-center text-sm text-gray-500"
              >
                View {title}
              </Link>
            </div>
          </ButtonPopover>
        );
      }
    }
    return <Fragment>-</Fragment>;
  }

  return (
    <SelectWrapper className={className}>
      <Select
        closeMenuOnSelect={true}
        styles={selectStyles}
        value={value}
        options={options}
        onChange={onChangeHandler}
        isClearable
        isLoading={loading}
        onMenuOpen={() => {
          refetch();
        }}
      />
    </SelectWrapper>
  );
}
